import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { ChevronDown, ChevronUp, Info, AlertTriangle, ExternalLink } from 'lucide-react';
export function PaymentRecoveryPage() {
  const navigate = useNavigate();
  // State
  const [smartRecoveryEnabled, setSmartRecoveryEnabled] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Retry attempts state
  const [retry1Enabled, setRetry1Enabled] = useState(true);
  const [retry1Days, setRetry1Days] = useState(3);
  const [retry1Time, setRetry1Time] = useState('9:00 AM');
  const [retry1Email, setRetry1Email] = useState(true);
  const [retry1SMS, setRetry1SMS] = useState(true);
  const [retry1Expanded, setRetry1Expanded] = useState(false);
  const [retry2Enabled, setRetry2Enabled] = useState(true);
  const [retry2Days, setRetry2Days] = useState(5);
  const [retry2Time, setRetry2Time] = useState('9:00 AM');
  const [retry2Email, setRetry2Email] = useState(true);
  const [retry2SMS, setRetry2SMS] = useState(true);
  const [retry2Expanded, setRetry2Expanded] = useState(false);
  const [retry3Enabled, setRetry3Enabled] = useState(true);
  const [retry3Days, setRetry3Days] = useState(7);
  const [retry3Time, setRetry3Time] = useState('9:00 AM');
  const [retry3Email, setRetry3Email] = useState(true);
  const [retry3SMS, setRetry3SMS] = useState(false);
  const [retry3Expanded, setRetry3Expanded] = useState(false);
  const [retry4Enabled, setRetry4Enabled] = useState(false);
  const [retry4Days, setRetry4Days] = useState(10);
  const [retry4Time, setRetry4Time] = useState('9:00 AM');
  const [retry4Email, setRetry4Email] = useState(true);
  const [retry4SMS, setRetry4SMS] = useState(false);
  const [retry4Expanded, setRetry4Expanded] = useState(false);
  // Email templates state
  const [initialFailureEmail, setInitialFailureEmail] = useState(true);
  const [retryReminderEmail, setRetryReminderEmail] = useState(true);
  const [paymentRecoveredEmail, setPaymentRecoveredEmail] = useState(true);
  const [finalWarningEmail, setFinalWarningEmail] = useState(true);
  const [subscriptionOnHoldEmail, setSubscriptionOnHoldEmail] = useState(true);
  // SMS state
  const [smsEnabled, setSmsEnabled] = useState(false);
  // Payment link state
  const [paymentLinkEnabled, setPaymentLinkEnabled] = useState(true);
  const [linkExpiration, setLinkExpiration] = useState('7 days');
  // Internal notifications state
  const [notifyOnFailure, setNotifyOnFailure] = useState(true);
  const [notifyOnRecovery, setNotifyOnRecovery] = useState(false);
  const [notifyOnFallback, setNotifyOnFallback] = useState(true);
  const [dailySummary, setDailySummary] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState(['orders@acmewine.com']);
  const [newEmail, setNewEmail] = useState('');
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const handleStatCardClick = (type: string) => {
    console.log(`Navigate to ${type}`);
  };
  const handleAddEmail = () => {
    if (newEmail && newEmail.includes('@')) {
      setNotificationEmails([...notificationEmails, newEmail]);
      setNewEmail('');
      saveSetting('Email added');
    }
  };
  const handleRemoveEmail = (emailToRemove: string) => {
    setNotificationEmails(notificationEmails.filter(email => email !== emailToRemove));
    saveSetting('Email removed');
  };
  const daysOptions = [1, 2, 3, 5, 7, 10, 14];
  const timeOptions = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM'];
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-11952" data-mp="32hejb1aqtj27">
      {/* NavBar */}
      <div className="mb-6" data-id="element-11953" data-mp="2yh4a7frnmcj4">
        <NavBar tabs={[]} title="Payment Recovery" subtitle="Automate failed payment retries and reduce involuntary churn with smart dunning" data-id="element-11954" data-mp="2yft6ffy7l4s0" />
      </div>

      {/* Section 1: Recovery Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-11955" data-mp="37ahch8blyqhv">
        <StatCard label="Failed Payments" value={4} secondaryValue="$1,240 at risk" badge={{
        text: 'Action Needed',
        variant: 'error'
      }} onClick={() => handleStatCardClick('failed')} data-id="element-11956" data-mp="2jitwvmjsr1uw" />

        <StatCard label="In Recovery" value={3} secondaryValue="Active retry sequences" badge={{
        text: 'Retrying',
        variant: 'warning'
      }} data-id="element-11957" data-mp="2tit0r659u41f" />

        <StatCard label="Recovered This Month" value={12} secondaryValue="$2,847 recovered" trend={<TrendIndicator value={18} label="vs last month" direction="up" data-id="element-11959" data-mp="3sg0cmz3ytlw" />} data-id="element-11958" data-mp="8rkb3i1okzec" />

        <StatCard label="Recovery Rate" value="73%" secondaryValue="Industry avg: 65%" trend={<TrendIndicator value={5} label="vs last month" direction="up" data-id="element-11961" data-mp="1loj5qg1nc48y" />} data-id="element-11960" data-mp="3uk1wy9jbgj0" />
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-11962" data-mp="1qpyog1gcpyo6" />

      {/* Main Content */}
      <div className="space-y-6" data-id="element-11963" data-mp="1cq636lnlganl">
        {/* Section 2: Smart Recovery Toggle */}
        <Card data-id="element-11964" data-mp="1ho7iqwlt9rub">
          <div className="mb-6" data-id="element-11965" data-mp="1hnph1kgdqlf0">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11966" data-mp="1hp0ru488stfm">
              Smart Recovery
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11967" data-mp="25g682ki3hnqg">
              Automatically retry failed payments using optimized timing
            </p>
          </div>

          <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-11968" data-mp="1vnh03zlr6rzv">
            <div className="flex-1 pr-6" data-id="element-11969" data-mp="20mbjp3rjokl1">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-11970" data-mp="20l0dyyejh2ra">
                Enable Smart Recovery
              </h3>
              <p className="text-[14px] text-[#64748B]" data-id="element-11971" data-mp="2t460rpyyvo7o">
                When enabled, WinePik will automatically retry failed payments
                based on your configured schedule. Customers will be notified at
                each step.
              </p>
            </div>
            <button onClick={() => {
            setSmartRecoveryEnabled(!smartRecoveryEnabled);
            saveSetting();
          }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${smartRecoveryEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={smartRecoveryEnabled} data-id="element-11972" data-mp="2t1gko1z1unq8">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${smartRecoveryEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-11973" data-mp="2aafmvh9o0y89" />
            </button>
          </div>

          {smartRecoveryEnabled && <div className="mt-4 p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-11974" data-mp="20c9p9buw6e97">
              <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-11975" data-mp="1wbzjo0b0hpuv" />
              <p className="text-[14px] text-[#0369A1]" data-id="element-11976" data-mp="bsdltcuj97na">
                Smart Recovery analyzes payment failure reasons and optimizes
                retry timing for best results. Most failed payments recover
                within 2-3 retry attempts.
              </p>
            </div>}
        </Card>

        {/* Section 3: Retry Schedule */}
        <Card data-id="element-11977" data-mp="pqkca7x5k5cx">
          <div className="mb-6" data-id="element-11978" data-mp="3oau16cjyd4b1">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-11979" data-mp="3oblnewrntpon">
              Retry Schedule
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-11980" data-mp="3ta6iyivyvl6r">
              Configure when and how often to retry failed payments
            </p>
          </div>

          <div className={`space-y-3 ${!smartRecoveryEnabled ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-11981" data-mp="6ukjiuwcoxsa">
            {/* Retry 1 */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-11982" data-mp="35jjanwi52tjo">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry1Expanded(!retry1Expanded)} data-id="element-11983" data-mp="3ai41j5bi40vg">
                <div className="flex items-center gap-3" data-id="element-11984" data-mp="1yxia1rzd4wji">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-11985" data-mp="1yz33fe9xy9fs">
                    Retry 1
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-11986" data-mp="27wddiwb0vsj4">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry1Enabled(!retry1Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry1Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-11987" data-mp="2mupc58hyhbcx">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry1Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-11988" data-mp="2rl5p6rhv6uyr" />
                  </button>
                  {retry1Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-11989" data-mp="30n57dn4m853a" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-11990" data-mp="30k9a810ldkfw" />}
                </div>
              </div>

              {retry1Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-11991" data-mp="3p7icj9y0ha6m">
                  <div className="grid grid-cols-2 gap-4" data-id="element-11992" data-mp="3a9ycmhfz257b">
                    <div data-id="element-11993" data-mp="3a9g8kyn2imou">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-11994" data-mp="3f8sthdudpjos">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-11995" data-mp="31gme3tz1vgqe">
                        <select value={retry1Days} onChange={e => {
                      setRetry1Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-11996" data-mp="367ur0zzl9k8b">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-11997" data-mp="19013curexdkm">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-11998" data-mp="1e1qd872wfl74">
                          days after initial failure
                        </span>
                      </div>
                    </div>

                    <div data-id="element-11999" data-mp="147gdg4bmnkmd">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12000" data-mp="1ryvja72m4k2d">
                        Time
                      </label>
                      <select value={retry1Time} onChange={e => {
                    setRetry1Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-12001" data-mp="1wrex9557ph7p">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-12002" data-mp="noajakjur77j">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-12003" data-mp="ptz249ivwkkk">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-12004" data-mp="zrwo0qd9u5a0">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12005" data-mp="zrmy12s87y4b">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-12006" data-mp="14owwt91i9m4t">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12007" data-mp="74a9wo6f3i8w">
                        <input type="checkbox" checked={retry1Email} onChange={e => {
                      setRetry1Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12008" data-mp="c1snirvdqln8" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12009" data-mp="3kidn5i24g2do">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12010" data-mp="3pf42u4a0kvyk">
                        <input type="checkbox" checked={retry1SMS} onChange={e => {
                      setRetry1SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12011" data-mp="26lkh84h7up6n" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12012" data-mp="1sl9pz0bzv8ky">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Retry 2 */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-12013" data-mp="1eueo47zwxrqv">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry2Expanded(!retry2Expanded)} data-id="element-12014" data-mp="19vtj7v9pujw3">
                <div className="flex items-center gap-3" data-id="element-12015" data-mp="14yl1fvshchvd">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-12016" data-mp="zyynfk4tuxli">
                    Retry 2
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-12017" data-mp="34yqb1bgq63is">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry2Enabled(!retry2Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry2Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-12018" data-mp="34y85tpc34eyq">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry2Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-12019" data-mp="2hbrwwkg2v7o1" />
                  </button>
                  {retry2Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-12020" data-mp="2ca2kb0d7ilxu" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-12021" data-mp="2caugodemonp6" />}
                </div>
              </div>

              {retry2Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-12022" data-mp="5i1rhyib4dbs">
                  <div className="grid grid-cols-2 gap-4" data-id="element-12023" data-mp="ai67zoj7t820">
                    <div data-id="element-12024" data-mp="1v08hrz7sqax9">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12025" data-mp="1v1jghkmdwshi">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-12026" data-mp="24qjlplrhck45">
                        <select value={retry2Days} onChange={e => {
                      setRetry2Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-12027" data-mp="24sw9ifjx5op4">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-12028" data-mp="2tg0727gx8eb9">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-12029" data-mp="32fdgj051p2vz">
                          days after Retry 1
                        </span>
                      </div>
                    </div>

                    <div data-id="element-12030" data-mp="3cbgtl11fqlxp">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12031" data-mp="3cax7d84r5bdv">
                        Time
                      </label>
                      <select value={retry2Time} onChange={e => {
                    setRetry2Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-12032" data-mp="3l5bnfcx3wpy1">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-12033" data-mp="i2gsfm8qlt8s">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-12034" data-mp="e0dfsuac0ilx">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-12035" data-mp="15qhzsru6j3mm">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12036" data-mp="15pyj1eg9mla6">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-12037" data-mp="1u71usdjqi7r4">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12038" data-mp="1fhxe50sl8bl4">
                        <input type="checkbox" checked={retry2Email} onChange={e => {
                      setRetry2Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12039" data-mp="1fft19tcpo28p" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12040" data-mp="2d55wimxal4iw">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12041" data-mp="236ozp9bo9vox">
                        <input type="checkbox" checked={retry2SMS} onChange={e => {
                      setRetry2SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12042" data-mp="msqgs54vds1g" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12043" data-mp="310zr8gxqivbe">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Retry 3 */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-12044" data-mp="3ax2q9dxaidu4">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry3Expanded(!retry3Expanded)} data-id="element-12045" data-mp="2na4bqlsqdfyu">
                <div className="flex items-center gap-3" data-id="element-12046" data-mp="2w2pvo583lsp9">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-12047" data-mp="2rbg777yacvey">
                    Retry 3
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-12048" data-mp="23k2ev204wztz">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry3Enabled(!retry3Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry3Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-12049" data-mp="1zkjuc7c86qro">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry3Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-12050" data-mp="13cfk6pelxhai" />
                  </button>
                  {retry3Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-12051" data-mp="1d8a73wpv5yj3" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-12052" data-mp="18b0i2i6dzovs" />}
                </div>
              </div>

              {retry3Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-12053" data-mp="1m9gqqent7pgk">
                  <div className="grid grid-cols-2 gap-4" data-id="element-12054" data-mp="1hb6tlyiiyesb">
                    <div data-id="element-12055" data-mp="1h9lzub41azvi">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12056" data-mp="3o1czxhmi8n86">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-12057" data-mp="3j4mlszu9g1pe">
                        <select value={retry3Days} onChange={e => {
                      setRetry3Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-12058" data-mp="1ykhj9xd56rlq">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-12059" data-mp="35869141pnbtn">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-12060" data-mp="d1nj2944bd6n">
                          days after Retry 2
                        </span>
                      </div>
                    </div>

                    <div data-id="element-12061" data-mp="40z5tgmjtgut">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12062" data-mp="41r10qc8hckk">
                        Time
                      </label>
                      <select value={retry3Time} onChange={e => {
                    setRetry3Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-12063" data-mp="10tcdbv0i2bq8">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-12064" data-mp="210zn2ankhiu0">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-12065" data-mp="2ax2nnc2rt9eu">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-12066" data-mp="2kn5p80nyjvos">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12067" data-mp="2kmdx4ztkkg39">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-12068" data-mp="2on64tomsmcxf">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12069" data-mp="2tovjrfd4zaqs">
                        <input type="checkbox" checked={retry3Email} onChange={e => {
                      setRetry3Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12070" data-mp="2g0lhi9venw5j" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12071" data-mp="2krlc38h1y24v">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12072" data-mp="1x4c3fmki75lj">
                        <input type="checkbox" checked={retry3SMS} onChange={e => {
                      setRetry3SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12073" data-mp="1t2qiddxtfl09" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12074" data-mp="3iekdtxmlq09m">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Retry 4 (Final) */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-12075" data-mp="1495j501t1tmr">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry4Expanded(!retry4Expanded)} data-id="element-12076" data-mp="q8uwyabfx37x">
                <div className="flex items-center gap-3" data-id="element-12077" data-mp="1ix8ukoym9xiy">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-12078" data-mp="1ixs8zvp263s8">
                    Retry 4 (Final)
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-12079" data-mp="197yxeuox1onz">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry4Enabled(!retry4Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry4Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-12080" data-mp="195cqmx9und6t">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry4Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-12081" data-mp="2gj39lcetu82j" />
                  </button>
                  {retry4Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-12082" data-mp="26jkq0x2twob0" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-12083" data-mp="26mgnyleofczg" />}
                </div>
              </div>

              {retry4Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-12084" data-mp="1xjfoh3kbgk4r">
                  <div className="grid grid-cols-2 gap-4" data-id="element-12085" data-mp="1ssz9hf0ukqga">
                    <div data-id="element-12086" data-mp="1sreexpej97if">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12087" data-mp="1sr4ltsy6yk8i">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-12088" data-mp="1ivuhuzo8g2s3">
                        <select value={retry4Days} onChange={e => {
                      setRetry4Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-12089" data-mp="1dvglwfzwojaf">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-12090" data-mp="3gi3s3stncwl8">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-12091" data-mp="2j2pxya19jm88">
                          days after Retry 3
                        </span>
                      </div>
                    </div>

                    <div data-id="element-12092" data-mp="1hf0d0wvgqty7">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12093" data-mp="1cfnkbvhwusnp">
                        Time
                      </label>
                      <select value={retry4Time} onChange={e => {
                    setRetry4Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-12094" data-mp="1qg84ow3h4wjb">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-12095" data-mp="32q5s0zl78uuv">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-12096" data-mp="2nuzgxy78y5uo">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-12097" data-mp="2eygsoz9yn64f">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12098" data-mp="2jqruk7o9h1zm">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-12099" data-mp="253ox20zxutca">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12100" data-mp="2a0oytzpr99xl">
                        <input type="checkbox" checked={retry4Email} onChange={e => {
                      setRetry4Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12101" data-mp="1w1pcwztmn954" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12102" data-mp="n3dvdls5nwac">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-12103" data-mp="wxmb18w37ur3">
                        <input type="checkbox" checked={retry4SMS} onChange={e => {
                      setRetry4SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-12104" data-mp="11zbpkyiiqqjx" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-12105" data-mp="1fpf2gyvid7wp">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="p-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-md flex items-start gap-2" data-id="element-12106" data-mp="1pow5f8jdl7oz">
                    <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" data-id="element-12107" data-mp="1tq9ahgen96uu" />
                    <p className="text-[13px] text-[#92400E]" data-id="element-12108" data-mp="3e8a57puqsxsy">
                      This is the final attempt before fallback action
                    </p>
                  </div>
                </div>}
            </div>
          </div>
        </Card>

        {/* Section 4: Fallback Action */}
        <Card data-id="element-12109" data-mp="308r7lqzk54uw">
          <div className="mb-6" data-id="element-12110" data-mp="309ag6h3bigw9">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12111" data-mp="30a2dbwckcmon">
              Fallback Action
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-12112" data-mp="3swn4owt1gys7">
              What happens if all retry attempts fail
            </p>
          </div>

          <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-12113" data-mp="3o43pjvepdvla">
            <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-12114" data-mp="3j625b75jjq88" />
            <p className="text-[14px] text-[#0369A1]" data-id="element-12115" data-mp="khmvpl5hqguc">
              After all retry attempts are exhausted, the subscription will be
              placed On Hold. The customer will be notified and can update their
              payment method to resume.
            </p>
          </div>
        </Card>

        {/* Section 5: Customer Notifications */}
        <Card data-id="element-12116" data-mp="2e14zit0kx97n">
          <div className="mb-6" data-id="element-12117" data-mp="2085lkvgg3n27">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12118" data-mp="206ulp6q4ezgd">
              Customer Notifications
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-12119" data-mp="2555tlbwuuzvy">
              Configure what customers receive during the recovery process
            </p>
          </div>

          <div className="space-y-4 mb-6" data-id="element-12120" data-mp="1v9vqekuq0mgl">
            <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider" data-id="element-12121" data-mp="1v6q1p6q07mle">
              Email Templates
            </h3>

            {[{
            id: 'initial',
            label: 'Initial Failure Email',
            description: 'Sent immediately when a payment fails',
            enabled: initialFailureEmail,
            setEnabled: setInitialFailureEmail
          }, {
            id: 'retry',
            label: 'Retry Reminder Email',
            description: 'Sent before each retry attempt',
            enabled: retryReminderEmail,
            setEnabled: setRetryReminderEmail
          }, {
            id: 'recovered',
            label: 'Payment Recovered Email',
            description: 'Sent when payment is successfully recovered',
            enabled: paymentRecoveredEmail,
            setEnabled: setPaymentRecoveredEmail
          }, {
            id: 'warning',
            label: 'Final Warning Email',
            description: 'Sent before fallback action is executed',
            enabled: finalWarningEmail,
            setEnabled: setFinalWarningEmail
          }, {
            id: 'onhold',
            label: 'Subscription On Hold Email',
            description: 'Sent when subscription is placed on hold due to failed payments',
            enabled: subscriptionOnHoldEmail,
            setEnabled: setSubscriptionOnHoldEmail
          }].map(template => <div key={template.id} className="flex items-center justify-between py-3 border-b border-[#E2E8F0] last:border-0" data-id="element-12122" data-mp="2k3qcidghc4vk">
                <div className="flex-1" data-id="element-12123" data-mp="2qhnpeorwon0h">
                  <div className="flex items-center gap-3" data-id="element-12124" data-mp="2qgchqdvcav43">
                    <h4 className="text-[14px] font-medium text-[#0F172A]" data-id="element-12125" data-mp="2uizgkyqfhgas">
                      {template.label}
                    </h4>
                    <button onClick={e => {
                  e.stopPropagation();
                  template.setEnabled(!template.enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${template.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-12126" data-mp="2zi2abn0yk03g">
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${template.enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-12127" data-mp="2gjpy1ser2a3n" />
                    </button>
                  </div>
                  <p className="text-[13px] text-[#64748B] mt-1" data-id="element-12128" data-mp="1o2x80k4qo3kd">
                    {template.description}
                  </p>
                </div>
                <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors flex items-center gap-1" data-id="element-12129" data-mp="1ghtkpwjdpd0q">
                  Preview & Edit
                  <ExternalLink className="w-3 h-3" data-id="element-12130" data-mp="16kf3hupmsk7d" />
                </button>
              </div>)}
          </div>

          <div className="pt-4 border-t border-[#E2E8F0]" data-id="element-12131" data-mp="12jlk9en4vtkt">
            <div className="flex items-center justify-between mb-3" data-id="element-12132" data-mp="xlbksrudm6te">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider" data-id="element-12133" data-mp="subp1pwztush">
                SMS Notifications
              </h3>
              <button onClick={() => {
              setSmsEnabled(!smsEnabled);
              saveSetting();
            }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${smsEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-12134" data-mp="nw0e1buype8o">
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${smsEnabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-12135" data-mp="9vpnyb1h83ok" />
              </button>
            </div>

            {smsEnabled && <div className="space-y-3" data-id="element-12136" data-mp="49p3n78ctwwn">
                <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-12137" data-mp="4aruxg9la1d3">
                  <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-12138" data-mp="3mh630yl6fh3k" />
                  <div className="flex-1" data-id="element-12139" data-mp="3reeqnep3gdc7">
                    <p className="text-[14px] text-[#0369A1] mb-2" data-id="element-12140" data-mp="iylbp4aev9x9">
                      SMS requires Klaviyo or Postscript integration. Configure
                      in Notifications settings.
                    </p>
                    <button onClick={() => navigate('/integrations')} className="text-[14px] font-medium text-[#0369A1] hover:text-[#0284C7] transition-colors flex items-center gap-1 underline" data-id="element-12141" data-mp="99be58wssz1r">
                      Configure SMS Integration
                      <ExternalLink className="w-3 h-3" data-id="element-12142" data-mp="11v4ap0ri3hg3" />
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </Card>

        {/* Section 6: Update Payment Link */}
        <Card data-id="element-12143" data-mp="2mf9jgkz7pns7">
          <div className="mb-6" data-id="element-12144" data-mp="2mfrlxzcgwy8k">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12145" data-mp="2meq9prjpu5p9">
              Update Payment Link
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-12146" data-mp="308qybsgyecbw">
              Allow customers to easily update their payment method
            </p>
          </div>

          <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-12147" data-mp="28gi3et0shs1f">
            <div className="flex-1 pr-6" data-id="element-12148" data-mp="23h5g6760aiyb">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-12149" data-mp="23ignggynu8u6">
                Include payment update link in emails
              </h3>
              <p className="text-[14px] text-[#64748B]" data-id="element-12150" data-mp="2cgsl32xy26w4">
                Add a secure link in recovery emails that takes customers
                directly to update their payment method
              </p>
            </div>
            <button onClick={() => {
            setPaymentLinkEnabled(!paymentLinkEnabled);
            saveSetting();
          }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${paymentLinkEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={paymentLinkEnabled} data-id="element-12151" data-mp="3nvfm2usn8z0f">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${paymentLinkEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-12152" data-mp="plneeuigkfix" />
            </button>
          </div>

          {paymentLinkEnabled && <div className="pt-4" data-id="element-12153" data-mp="zkcv4jymbtxl">
              <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12154" data-mp="14hd535yl9gtz">
                Link expiration
              </label>
              <select value={linkExpiration} onChange={e => {
            setLinkExpiration(e.target.value);
            saveSetting();
          }} className="w-full max-w-xs h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundPosition: 'right 12px center',
            backgroundSize: '16px 16px'
          }} data-id="element-12155" data-mp="18if4pnwf3uwh">
                <option value="24 hours" data-id="element-12156" data-mp="3goqrqc45cd79">24 hours</option>
                <option value="48 hours" data-id="element-12157" data-mp="3co6uz9nhjbln">48 hours</option>
                <option value="7 days" data-id="element-12158" data-mp="3cr2qkrbv1o6x">7 days</option>
                <option value="30 days" data-id="element-12159" data-mp="37pdhhh877klh">30 days</option>
                <option value="Never" data-id="element-12160" data-mp="219tbegzrlibf">Never</option>
              </select>
              <p className="text-[12px] text-[#64748B] mt-1" data-id="element-12161" data-mp="218036x9i6q1b">
                How long the payment update link remains valid
              </p>
            </div>}
        </Card>

        {/* Section 7: Internal Notifications */}
        <Card data-id="element-12162" data-mp="1kq6lxea9lqog">
          <div className="mb-6" data-id="element-12163" data-mp="1kpdl0txzlve8">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12164" data-mp="1kqf15ctv182t">
              Internal Notifications
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-12165" data-mp="1fswxo1ym3l27">
              Get notified about payment recovery events
            </p>
          </div>

          <div className="space-y-6" data-id="element-12166" data-mp="15swgomo7th16">
            {[{
            id: 'failure',
            title: 'Notify on payment failure',
            description: 'Receive notification when a payment fails',
            enabled: notifyOnFailure,
            setEnabled: setNotifyOnFailure
          }, {
            id: 'recovery',
            title: 'Notify on successful recovery',
            description: 'Receive notification when a payment is recovered',
            enabled: notifyOnRecovery,
            setEnabled: setNotifyOnRecovery
          }, {
            id: 'fallback',
            title: 'Notify on fallback action',
            description: 'Receive notification when a subscription is placed on hold due to failed payments',
            enabled: notifyOnFallback,
            setEnabled: setNotifyOnFallback
          }, {
            id: 'summary',
            title: 'Daily recovery summary',
            description: 'Receive a daily email summarizing payment recovery status',
            enabled: dailySummary,
            setEnabled: setDailySummary
          }].map(notification => <div key={notification.id} className="flex items-start justify-between py-4 border-b border-[#E2E8F0] last:border-0" data-id="element-12167" data-mp="3qcl1puvm04sp">
                <div className="flex-1 pr-6" data-id="element-12168" data-mp="3vamnwooc0k8e">
                  <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-12169" data-mp="3x9u2u0if3z4">
                    {notification.title}
                  </h3>
                  <p className="text-[14px] text-[#64748B]" data-id="element-12170" data-mp="32mqtxrq5d6fb">
                    {notification.description}
                  </p>
                </div>
                <button onClick={() => {
              notification.setEnabled(!notification.enabled);
              saveSetting();
            }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={notification.enabled} data-id="element-12171" data-mp="2zlxc7cyvnxwv">
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-12172" data-mp="2go4b8g7tdy24" />
                </button>
              </div>)}
          </div>

          <div className="pt-6 border-t border-[#E2E8F0] mt-6" data-id="element-12173" data-mp="2qmk5ut61vpgd">
            <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-12174" data-mp="2ll4igugeh6ut">
              Send notifications to
            </label>
            <div className="flex items-center gap-2 mb-2" data-id="element-12175" data-mp="5x4hs5y7ueuf">
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyPress={e => {
              if (e.key === 'Enter') {
                handleAddEmail();
              }
            }} placeholder="email@example.com" className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-12176" data-mp="uvlp0q1qwo3" />
              <button onClick={handleAddEmail} className="h-[40px] px-4 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-12177" data-mp="3gv6thmwgpqw">
                + Add
              </button>
            </div>
            {notificationEmails.length === 0 && <p className="text-[12px] text-[#64748B] mb-3" data-id="element-12178" data-mp="ief92gsew6td">
                Store owner email will be used by default
              </p>}
            <div className="flex flex-wrap gap-2 mt-3" data-id="element-12179" data-mp="mdecty2epxj8">
              {notificationEmails.map(email => <span key={email} className="inline-flex items-center gap-2 px-3 py-1 bg-[#E0F2FE] text-[#0369A1] rounded-full text-[13px]" data-id="element-12180" data-mp="1a38owdewji2f">
                  {email}
                  <button onClick={() => handleRemoveEmail(email)} className="hover:text-[#EF4444] transition-colors" data-id="element-12181" data-mp="3ncuotzv7offi">
                    ×
                  </button>
                </span>)}
            </div>
          </div>
        </Card>
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-12182" data-mp="39dla74mnltdn">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-12183" data-mp="9xy2hyd2stpf">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-12184" data-mp="4z4z4yri6j2z" />
            <div className="flex-1" data-id="element-12185" data-mp="73gmv3qrw4i">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-12186" data-mp="624enazp3l7">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-12187" data-mp="3s9uw2e3sro87">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}